import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { profileApi } from '@/features/student/api/profile.api'

export const PROFILE_QUERY_KEY = ['student', 'profile'] as const
export const PROFILE_FIELDS_QUERY_KEY = ['student', 'profile-fields'] as const

export function useStudentProfileQuery() {
  return useQuery({ queryKey: PROFILE_QUERY_KEY, queryFn: profileApi.get })
}

export function useProfileFieldsQuery() {
  return useQuery({
    queryKey: PROFILE_FIELDS_QUERY_KEY,
    queryFn: profileApi.fields,
    staleTime: 60 * 60 * 1000,
  })
}

function useInvalidateProfile() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY })
}

export function useUpdateProfile() {
  const invalidate = useInvalidateProfile()
  return useMutation({
    mutationFn: profileApi.update,
    onSuccess: () => void invalidate(),
  })
}

export function useUpdateVisibility() {
  const invalidate = useInvalidateProfile()
  return useMutation({
    mutationFn: profileApi.updateVisibility,
    onSuccess: () => void invalidate(),
  })
}

export function useUpdateProfileFields() {
  const invalidate = useInvalidateProfile()
  return useMutation({
    mutationFn: profileApi.updateFields,
    onSuccess: () => void invalidate(),
  })
}

export function useUploadResume() {
  const invalidate = useInvalidateProfile()
  return useMutation({
    mutationFn: profileApi.uploadResume,
    onSuccess: () => void invalidate(),
  })
}

export function useUploadPhoto() {
  const invalidate = useInvalidateProfile()
  return useMutation({
    mutationFn: profileApi.uploadPhoto,
    onSuccess: () => void invalidate(),
  })
}

// --- Repeatable sections: one create/update/remove trio per section, all
// invalidating the same profile query since it embeds every section. ---

function useSectionMutations(section: {
  create: (payload: Record<string, unknown>) => Promise<unknown>
  update: (id: number, payload: Record<string, unknown>) => Promise<unknown>
  remove: (id: number) => Promise<unknown>
}) {
  const invalidate = useInvalidateProfile()
  const create = useMutation({ mutationFn: section.create, onSuccess: () => void invalidate() })
  const update = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Record<string, unknown> }) =>
      section.update(id, payload),
    onSuccess: () => void invalidate(),
  })
  const remove = useMutation({ mutationFn: section.remove, onSuccess: () => void invalidate() })
  return { create, update, remove }
}

export function useExperienceMutations() {
  return useSectionMutations(profileApi.experiences)
}
export function useEducationMutations() {
  return useSectionMutations(profileApi.educations)
}
export function useSkillMutations() {
  return useSectionMutations(profileApi.skills)
}
export function useCertificationMutations() {
  return useSectionMutations(profileApi.certifications)
}
export function useProjectMutations() {
  return useSectionMutations(profileApi.projects)
}
export function useLanguageMutations() {
  return useSectionMutations(profileApi.languages)
}
export function useSocialLinkMutations() {
  return useSectionMutations(profileApi.socialLinks)
}
